import { getNearbyPincodes as getNearbyPincodesFromService } from "./pincodeService";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org";
const UA = "PGApp/1.0 (pgfinder@example.com)";
const API_BASE_URL = "https://ciplpgapi.azurewebsites.net";

// Cache for geocoding results
const geocodeCache = new Map();

// ========== Helper: Get accurate pincode from coordinates ==========
// If the point (e.g. a bare city centroid like "Karnal" as a whole) has
// no addr:postcode tagged in OSM, that's a legitimate "no single pincode"
// answer - a whole city naturally spans many pincodes, so we don't chase
// this artificially. Returning null here is correct, expected behavior.
const getPincodeFromCoordinates = async (lat, lng) => {
  try {
    const reverseUrl = `${NOMINATIM_URL}/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=18`;
    const response = await fetch(reverseUrl, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data?.address?.postcode || data?.address?.postal_code || null;
  } catch (error) {
    console.warn("Reverse geocode error:", error);
    return null;
  }
};

// ========== MAIN: Geocode place (pan-India, no hardcoded city bias) ==========
export const geocodePlace = async (query) => {
  const cacheKey = query.toLowerCase().trim();
  if (geocodeCache.has(cacheKey)) return geocodeCache.get(cacheKey);

  const searchQuery = query.trim();

  console.log(`\n🔍 Geocoding: "${searchQuery}"`);

  // IMPORTANT: no city name is ever appended here, and no bounding box
  // is applied. Locking the search to a viewbox/city is exactly what
  // caused "karnal" (a city in Haryana) to resolve to "GT Karnal Road,
  // Delhi" - Nominatim was never allowed to look outside Delhi in the
  // first place. Restricting to India as a whole (not a city) keeps
  // results relevant without breaking non-Delhi searches.
  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(
    searchQuery
  )}&format=json&limit=5&addressdetails=1&countrycodes=in&dedupe=1`;

  console.log(`  Trying: "${searchQuery}"`);

  let bestResult = null;
  let bestLat = null;
  let bestLng = null;

  try {
    const response = await fetch(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      if (data && data.length > 0) {
        // Nominatim already ranks by relevance/importance across all of India
        bestResult = data[0];
        bestLat = parseFloat(bestResult.lat);
        bestLng = parseFloat(bestResult.lon);
        console.log(`    Found: ${bestResult.display_name?.split(",")[0]}`);
      }
    }
  } catch (e) {
    console.warn(`    Error:`, e.message);
  }

  if (!bestResult) {
    console.log(`❌ No results found for "${searchQuery}"`);
    return null;
  }

  console.log(`✅ Best match: ${bestResult.display_name?.split(",")[0]}`);
  console.log(`📍 Coordinates: ${bestLat}, ${bestLng}`);

  // Get accurate pincode via reverse geocoding
  let pincode = await getPincodeFromCoordinates(bestLat, bestLng);

  // If reverse geocode failed, fall back to the forward result's own address block
  if (!pincode && bestResult.address?.postcode) {
    pincode = bestResult.address.postcode;
  }

  if (pincode) {
    console.log(`  📮 Pincode: ${pincode}`);
  } else {
    // No static database fallback anymore - if OSM genuinely has no
    // postcode for this spot, we honestly report "not found" instead of
    // silently snapping to whatever Delhi pincode happens to be nearest.
    console.log(`  ⚠️ No pincode available from OSM for this location`);
  }

  const result = {
    lat: bestLat,
    lng: bestLng,
    pincode: pincode,
    label: bestResult.display_name?.split(",")[0] || searchQuery,
    fullAddress: bestResult.display_name,
  };

  console.log(
    `📍 Final: ${result.label} | Pincode: ${result.pincode || "NOT FOUND"}\n`
  );

  geocodeCache.set(cacheKey, result);
  return result;
};

// ========== MAIN SEARCH FUNCTION ==========
export const performLocationSearch = async (
  searchQuery,
  gender,
  price,
  sharing,
  pgType = null,
  pgCity = null // no longer defaults to "Delhi" - pan-India now
) => {
  console.log(`\n🔍 ========== NEW SEARCH ==========`);
  console.log(`🔎 "${searchQuery}"`);
  console.log(
    `📊 Filters: Gender=${gender}, Price=${price}, Sharing=${sharing}, PGType=${pgType}`
  );

  let geoResult = null;
  let pincodeInfo = null;
  let allPincodes = null;

  try {
    geoResult = await geocodePlace(searchQuery);
    if (geoResult && geoResult.pincode) {
      pincodeInfo = {
        pincode: geoResult.pincode,
        area: geoResult.label,
        coordinates: { lat: geoResult.lat, lng: geoResult.lng },
      };
      console.log(`📮📍 Pincode detected: ${geoResult.pincode} - ${geoResult.label}`);

      allPincodes = [geoResult.pincode];

      // Nearby pincodes are now found dynamically from the coordinates,
      // NOT looked up in a static per-city table - this is what makes
      // the pan-India rollout possible without pre-loading pincode data.
      try {
        const nearbyPincodes = await getNearbyPincodesFromService(
          geoResult.lat,
          geoResult.lng,
          3
        );
        if (nearbyPincodes && nearbyPincodes.length > 0) {
          console.log(`🔄 Nearby pincodes: ${nearbyPincodes.join(", ")}`);
          pincodeInfo.nearbyPincodes = nearbyPincodes;
          nearbyPincodes.forEach((p) => {
            if (!allPincodes.includes(p)) {
              allPincodes.push(p);
            }
          });
        }
      } catch (error) {
        console.log("⚠️ Could not fetch nearby pincodes");
      }
    } else {
      console.log(`⚠️ No pincode found for "${searchQuery}"`);
    }
  } catch (error) {
    console.log(`⚠️ Geocoding error:`, error.message);
  }

  let requestBody = {
    SearchText: `${searchQuery}`,
    PGRentRange: price || null,
    PGType: pgType || null,
  };

  if (allPincodes && allPincodes.length > 0) {
    requestBody.PinCode = allPincodes;
    console.log(`📮 Including ${allPincodes.length} pincode(s) in API request:`, allPincodes);
  } else {
    requestBody.PinCode = null;
    console.log(`⚠️ No pincodes found - sending PinCode: null`);
  }

  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("📍 SEARCH LOCATION:", searchQuery);
  if (pincodeInfo) {
    console.log("📮 Main Pincode:", pincodeInfo.pincode);
    console.log("📍 Area:", pincodeInfo.area);
    if (pincodeInfo.nearbyPincodes) {
      console.log("🔄 Nearby Pincodes:", pincodeInfo.nearbyPincodes);
    }
  } else {
    console.log("📮 Pincode: NOT FOUND for this location");
  }
  console.log("📨 API REQUEST BODY:", JSON.stringify(requestBody, null, 2));
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

  try {
    const apiResponse = await fetch(`${API_BASE_URL}/pgmaster/listing-landing-page`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!apiResponse.ok) {
      throw new Error(`API responded with status ${apiResponse.status}`);
    }

    const apiData = await apiResponse.json();
    console.log(`📦 API returned ${apiData?.length || 0} PGs`);

    if (apiData && Array.isArray(apiData) && apiData.length > 0) {
      const pincodesInResults = new Map();
      apiData.forEach((pg) => {
        const pgPincode = pg.PinCode || pg.pincode || "unknown";
        pincodesInResults.set(pgPincode, (pincodesInResults.get(pgPincode) || 0) + 1);
      });
      console.log("📊 Pincode distribution in results:");
      pincodesInResults.forEach((count, pincode) => {
        console.log(`   - ${pincode}: ${count} PG${count > 1 ? "s" : ""}`);
      });
    }

    return {
      success: true,
      results: apiData || [],
      resultCount: apiData?.length || 0,
      searchedLocation: searchQuery,
      appliedFilters: { gender, price, sharing, pgType, pgCity },
      pincodeInfo: pincodeInfo,
      pincodesSent: allPincodes,
      coordinates: geoResult ? { lat: geoResult.lat, lng: geoResult.lng } : null,
      locationName: geoResult?.label || searchQuery,
      nearbyPincodes: pincodeInfo?.nearbyPincodes || [],
      apiEndpoint: "listing-landing-page",
      requestBody: requestBody,
    };
  } catch (error) {
    console.error("API Error:", error);
    return {
      success: false,
      error: "Failed to fetch PG listings",
      resultCount: 0,
      details: error.message,
      searchedLocation: searchQuery,
      requestBody: requestBody,
      pincodeInfo: pincodeInfo,
      pincodesSent: allPincodes,
    };
  }
};

// ========== SUGGESTIONS ==========
// Also de-biased from Delhi - pan-India suggestions now.
export const getLocationSuggestions = async (query, limit = 8) => {
  if (!query || query.length < 2) return [];

  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(
    query
  )}&format=json&limit=${limit}&addressdetails=1&countrycodes=in&dedupe=1`;

  try {
    const response = await fetch(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data.map((item) => ({
      id: item.place_id,
      name: item.display_name.split(",")[0],
      area:
        item.address?.suburb ||
        item.address?.neighbourhood ||
        item.address?.city ||
        item.address?.state ||
        "",
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      pincode: item.address?.postcode || null,
      fullAddress: item.display_name,
    }));
  } catch (error) {
    console.error("Suggestions error:", error);
    return [];
  }
};